import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { proposals } from "@/lib/api";

const RFP_TYPES = ["RFP", "RFI", "RFQ", "SOW", "PWS"];
const inp = {
  width: "100%", padding: "8px 10px", border: "1.5px solid var(--border)",
  borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", background: "var(--surface)",
};

function Field({ label, full, children }) {
  return (
    <label style={{ display: "block", gridColumn: full ? "1 / -1" : "auto" }}>
      <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

export default function NewResponse() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", agency: "", rfp_type: "RFP", naics: "", due_date: "", value_range: "", description: "" });
  const [reqs, setReqs] = useState([]);
  const [excerpt, setExcerpt] = useState("");
  const [filename, setFilename] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (file) => {
    if (!file) return;
    setParseError("");
    setParsing(true);
    try {
      const res = await proposals.uploadPreview(file);
      const m = res.metadata || {};
      setForm((p) => ({
        ...p,
        title: p.title || m.title || "",
        agency: p.agency || m.agency || "",
        rfp_type: m.rfp_type || p.rfp_type,
        naics: p.naics || m.naics || "",
        due_date: p.due_date || (m.due_date ? String(m.due_date).slice(0, 10) : ""),
        description: p.description || m.summary || "",
      }));
      setReqs(res.requirements || []);
      setExcerpt(res.text_excerpt || "");
      setFilename(file.name);
    } catch (e) {
      setParseError(e?.response?.data?.detail || "Could not parse this document.");
    } finally {
      setParsing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || !form.agency.trim()) { setError("Title and Agency are required."); return; }
    setSubmitting(true);
    try {
      const created = await proposals.create({
        title: form.title.trim(),
        agency: form.agency.trim(),
        rfp_type: form.rfp_type,
        naics: form.naics.trim() || null,
        due_date: form.due_date || null,
        value_range: form.value_range.trim() || null,
        description: form.description.trim() || null,
        requirements: reqs.length ? reqs : null,
        raw_text_excerpt: excerpt || null,
      });
      navigate(`/workspace?proposal=${created.proposal_id}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create the response.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page" data-testid="page-new-response">
      <div className="page-header">
        <div>
          <div className="page-title">New Response</div>
          <div className="page-subtitle">Upload a solicitation to auto-fill the details and extract requirements — or fill them in manually.</div>
        </div>
      </div>

      <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">1 · Upload solicitation (optional)</span></div>
          <div style={{ padding: 20 }}>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="nr-dropzone"
              style={{ border: `2px dashed ${dragOver ? "var(--primary)" : "var(--border)"}`, borderRadius: 10, padding: 24, textAlign: "center", cursor: "pointer", background: dragOver ? "var(--primary-light)" : "transparent" }}
            >
              <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.xlsx,.msg,.zip"
                onChange={(e) => onFile(e.target.files?.[0])} data-testid="nr-file-input" />
              {parsing ? (
                <div><span className="spinner" /> Parsing &amp; extracting requirements…</div>
              ) : filename ? (
                <div><strong>{filename}</strong><div className="page-subtitle">{reqs.length} requirements extracted · form auto-filled below · click to replace</div></div>
              ) : (
                <div><div style={{ fontSize: 28 }}>📄</div><strong>Drop a solicitation here, or click to upload</strong><div className="page-subtitle">PDF, DOCX, XLSX, MSG, TXT, or ZIP</div></div>
              )}
            </div>
            {parseError && <div className="err" style={{ marginTop: 10 }} data-testid="nr-parse-error">{parseError}</div>}
            {reqs.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Extracted requirements ({reqs.length})</div>
                <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
                  {reqs.slice(0, 40).map((r, i) => (
                    <div key={r.id || i} style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", fontSize: 12.5, display: "flex", gap: 8 }}>
                      <span className="pill pill-blue" style={{ flexShrink: 0 }}>{r.id || `REQ-${i + 1}`}</span>
                      <span>{r.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">2 · Response details</span></div>
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            <Field label="Title *"><input style={inp} value={form.title} onChange={onChange("title")} data-testid="nr-title" /></Field>
            <Field label="Agency *"><input style={inp} value={form.agency} onChange={onChange("agency")} data-testid="nr-agency" /></Field>
            <Field label="Type"><select style={inp} value={form.rfp_type} onChange={onChange("rfp_type")} data-testid="nr-type">{RFP_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
            <Field label="NAICS"><input style={inp} value={form.naics} onChange={onChange("naics")} data-testid="nr-naics" /></Field>
            <Field label="Due date"><input style={inp} type="date" value={form.due_date} onChange={onChange("due_date")} data-testid="nr-due" /></Field>
            <Field label="Value range"><input style={inp} value={form.value_range} onChange={onChange("value_range")} placeholder="$1M–$3M" /></Field>
            <Field label="Description" full><textarea style={{ ...inp, resize: "vertical" }} rows={3} value={form.description} onChange={onChange("description")} data-testid="nr-desc" /></Field>
          </div>
        </div>

        {error && <div className="err" data-testid="nr-error">{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" className="btn btn-ghost" onClick={() => navigate("/opportunities")}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="nr-submit">
            {submitting ? <span className="spinner white" /> : "Create response"}
          </button>
        </div>
      </form>
    </div>
  );
}
